import * as $ from 'jquery';
import { IDisposable, IEventAggregator, disposableSubscription } from 'common/barrels/core';
import { ISubscription } from 'common/barrels/rx';
import { Request, FileParameter } from 'common/request';
import * as path from 'shared/path';
import { ValidationSummary, WorkItemValidationFailed, ValidationSummaryAndDataChanges } from 'shared/data/validation';
import { standardRequest } from 'common/requestidiom';
import { DataRequest } from 'shared/data/datarequest';
import { ConcurrencyCheckFailed } from 'shared/messages/concurrencycheckfailed';

import { RequestTracker } from 'runtime/requesttracker';
import { IMethodRouter } from 'runtime/methodrouter';
import { IValueStore } from 'runtime/valuestore';
import { IResourceProvider } from 'common/resourceprovider';
import { CallQueue } from 'runtime/callqueue';
import { DialogManager } from 'runtime/dialogmanager';
import { UploadManager } from 'runtime/uploadmanager';
import { ValueStore } from 'runtime/valuestore';
import { Control } from 'runtime/controls/control';
import { WorkItemViewInfo } from 'runtime/data/workitemviewinfo';
import { ActivityParameter } from 'runtime/data/activityparameter';
import { ActivityFileParameter } from 'runtime/data/activityfileparameter';
import { UploadResult } from 'runtime/data/uploadresult';
import { SaveRequested } from 'runtime/events/saverequested';
import { SubmitRequested } from 'runtime/events/submitrequested';
import { SubmitCloneRequested } from 'runtime/events/submitclonerequested';
import { CancelRequested } from 'runtime/events/cancelrequested';
import { RefreshRequested } from 'runtime/events/refreshrequested';
import { ValidateRequested } from 'runtime/events/validaterequested';
import { ValidationSummaryRequested } from 'runtime/events/validationsummaryrequested';
import { ActivityExecutionRequested } from 'runtime/events/activityexecutionrequested';
import { ViewInfoRequested } from 'runtime/events/viewinforequested';
import { LookupItemsRequested } from 'runtime/events/lookupitemsrequested';
import { LookupItemRequested } from 'runtime/events/lookupitemrequested';
import { LookupItemByDisplayRequested } from 'runtime/events/lookupitembydisplayrequested';
import { FilteredLookupItemsRequested } from 'runtime/events/filteredlookupitemsrequested';
import { CropRequested } from 'runtime/events/croprequested';
import { FileUploadStarted } from 'runtime/events/fileuploadstarted';
import { FileUploadFinished } from 'runtime/events/fileuploadfinished';
import { CloneRequested } from 'runtime/events/clonerequested';
import { SaveCloneRequested } from 'runtime/events/saveclonerequested';
import { HostDefunct } from 'runtime/events/hostdefunct';
import { DialogRequested } from 'runtime/events/dialogrequested';
import { RuntimeHostRequested } from 'runtime/events/runtimehostrequested';
import { Busy } from 'runtime/events/busy';
import { NotBusy } from 'runtime/events/notbusy';
import { FileUploadPending } from 'runtime/events/fileuploadpending';
import { WorkItemSubmitted } from 'runtime/events/workitemsubmitted';
import { FileTooLarge } from 'runtime/messages/filetoolarge';
import { FileTypeNotAllowed } from 'runtime/messages/filetypenotallowed';
import { SessionError } from 'runtime/messages/sessionerror';
import { SaveResult } from 'runtime/data/saveresult';
import { SubmitResult } from 'runtime/data/submitresult';
import { ActivityUpdate } from 'runtime/data/activityupdate';
import { LookupItem } from 'runtime/data/lookupitem';
import { FieldDataUpdate } from 'runtime/data/fielddataupdate';
import { SessionDataUpdate } from 'runtime/data/sessiondataupdate';
import { WorkItemChangedDialog, WorkItemChangedDialogMode } from 'runtime/dialogs/workitemchangeddialog';
import { Serializer } from 'common/serializer';

export interface IViewModel extends IDisposable {

}

export class ViewModel implements IViewModel {
    eventAggregator: IEventAggregator;
    methods: IMethodRouter;
    values: IValueStore;

    private resources: IResourceProvider;

    private wivi: WorkItemViewInfo;
    private requests: RequestTracker;
    private subscriptions: IDisposable;
    private callQueue: CallQueue;
    private dialogManager: DialogManager;
    private uploadManager: UploadManager;
    private serializer: Serializer;
    private csrfToken: () => string;

    validationSummary: ValidationSummary;

    private uploading: boolean;

    constructor(
        eventAggregator: IEventAggregator,
        resources: IResourceProvider,
        methods: IMethodRouter,
        wivi: WorkItemViewInfo,
        requests: RequestTracker,
        serializer: Serializer,
        csrfToken: () => string
    ) {
        this.eventAggregator = eventAggregator;
        this.resources = resources;

        this.wivi = wivi;

        this.methods = methods;

        this.requests = requests;

        this.callQueue = new CallQueue();

        this.serializer = serializer;

        this.csrfToken = csrfToken;

        this.values = new ValueStore(this.wivi, this.requests, this.callQueue, this.eventAggregator, this.serializer, csrfToken);

        this.uploading = false;

        this.dialogManager = new DialogManager(this.eventAggregator);

        this.uploadManager = new UploadManager(this.eventAggregator, this.wivi.workItem.workItemId, this.serializer, csrfToken);

        var readonly = this.wivi.workItem.readOnly;

        this.subscriptions = disposableSubscription(
            this.eventAggregator.query(SaveRequested).filter(() => !readonly).subscribe(x => this.save(x.methods, x.done)),
            this.eventAggregator.query(SubmitRequested).filter(() => !readonly).subscribe(x => this.submit(x.context, x.methods, x.afterSubmit, true)),
            this.eventAggregator.query(SubmitCloneRequested).filter(() => !readonly).subscribe(x => this.submitClone(x.context, x.methods, x.afterSubmit)),
            this.eventAggregator.query(CancelRequested).filter(() => !readonly).subscribe(() => this.cancel()),
            this.eventAggregator.query(RefreshRequested).subscribe(x => this.refresh(x.done)),
            this.eventAggregator.query(ValidateRequested).filter(() => !readonly).subscribe(x => this.validate(x.context, x.ruleName)),
            this.eventAggregator.query(ValidationSummaryRequested).subscribe(x => { x.validationSummary = this.validationSummary }),
            this.eventAggregator.query(ActivityExecutionRequested).subscribe(x => this.executeActivity(x.activityName, x.context, x.parameters, x.onExecutionCompleted, x.retainResults)),
            this.eventAggregator.query(ViewInfoRequested).subscribe(x => this.provideViewInfo(x)),
            this.handleLookupItemByDisplayRequested(this.eventAggregator),
            this.handleFilteredLookupItemsRequested(this.eventAggregator),
            this.handleLookupItemsRequested(this.eventAggregator),
            this.handleLookupItemRequested(this.eventAggregator),
            this.eventAggregator.query(CropRequested).filter(() => !readonly).subscribe(x => this.crop(x)),
            this.eventAggregator.query(FileUploadStarted).filter(() => !readonly).subscribe(() => this.uploading = true),
            this.eventAggregator.query(FileUploadFinished).filter(() => !readonly).subscribe(() => this.uploading = false),
            this.eventAggregator.query(CloneRequested).subscribe(() => this.clone()),
            this.eventAggregator.query(SaveCloneRequested).filter(() => !readonly).subscribe(x => this.saveClone(x.methods, x.done))
        );
    }

    dispose() {
        this.subscriptions.dispose();
        this.eventAggregator.dispose();
        this.values.dispose();
        this.callQueue.dispose();
    }

    private handleError(messageObject?: any) {
        this.callQueue.clear();
        this.notBusy();
        this.methods.invoke('Document', 'Runtime error occurred', messageObject || null, () => { });
    }

    // primary workitem calls
    private save(methods: IMethodRouter, done: () => void) {
        var always = () => {
            this.notBusy();
            this.callQueue.proceed();
            done();
        };

        this.callQueue.enqueue(this.abortIfUploading(always, () => new Request(this.serializer, path.ui('save'))
            .before(() => this.busy())
            .args(() => this.constructRequestBaseObject())
            .handle(SaveResult, (x, args) => {
                this.wivi.workItem.versionId = x.versionId;
                this.wivi.workItem.freshlyCreated = false;
                this.updateLocalSession(args, x.dataChanges);
            })
            .handle(ConcurrencyCheckFailed, () => this.concurrencyError())
            .handle(FileTooLarge, () => methods.invoke('Document', 'maxFileSizeExceeded', null, () => { }))
            .handle(FileTypeNotAllowed, x => methods.invoke('Document', 'fileTypeNotAllowed', x.allowedTypes, () => { }))
            .retry(SessionError, () => this.resetSession())
            .final(always)
            .post(this.csrfToken())
        ));
    }

    private saveClone(methods: IMethodRouter, done: () => void) {
        this.save(methods, () => {
            this.clone();
        });
    }

    private submit(context, methods: IMethodRouter, afterSubmit: () => void, isFinal: boolean) {
        var tornDown = false;

        var always = () => {
            if (!tornDown)
                this.notBusy();

            this.callQueue.proceed();
        };

        this.callQueue.enqueue(this.abortIfUploading(always, () => new Request(this.serializer, path.ui('submit'))
            .before(() => this.busy())
            .args(() => this.constructRequestBaseObject())
            .handle(SubmitResult, (x, args) => {
                this.updateLocalSession(args, null);
                afterSubmit();
                // The item is already submitted; a failing confirmation must not stall HostDefunct or the call queue.
                try {
                    this.eventAggregator.publish(new WorkItemSubmitted(x.workItemId, x.title));
                } catch (e) {
                    console.error(e);
                }
                if (isFinal) {
                    this.eventAggregator.publish(new HostDefunct());
                    tornDown = true;
                }
            })
            .handle(WorkItemValidationFailed, (x, args) => {
                this.updateLocalSession(args, x.dataChanges);
                this.afterValidate(x.validationSummary, context);
            })
            .handle(ConcurrencyCheckFailed, () => this.concurrencyError())
            .handle(FileTooLarge, () => methods.invoke('Document', 'maxFileSizeExceeded', null, () => { }))
            .handle(FileTypeNotAllowed, x => methods.invoke('Document', 'fileTypeNotAllowed', x.allowedTypes, () => { }))
            .retry(SessionError, () => this.resetSession())
            .final(always)
            .post(this.csrfToken())
        ));
    }

    private submitClone(context, methods: IMethodRouter, afterSubmit: () => void) {
        this.submit(context, methods, () => {
            afterSubmit();
            this.clone();
        }, false);
    }

    private cancel() {
        this.callQueue.enqueue(() => {
            if (this.wivi.workItem.freshlyCreated)
                this.eventAggregator.publish(new HostDefunct());
            else
                new Request(this.serializer, path.ui('cancel'))
                    .before(() => this.busy())
                    .args(() => this.wivi.cacheToken)
                    .handle(null, () => this.eventAggregator.publish(new HostDefunct()))
                    .final(() => this.callQueue.proceed())
                    .post(this.csrfToken());
        });
    }

    private concurrencyError() {
        this.eventAggregator.publish(
            new DialogRequested(
                new WorkItemChangedDialog(WorkItemChangedDialogMode.DialogSpecific,
                    () => this.eventAggregator.publish(new HostDefunct()),
                    this.resources, this.resources.getCoreString('ErrWorkItemCannotBeRecovered'))
            )
        );
        // this is to stop the valuestore attempting to sync data after we already know that such attempts will fail
        this.values.dispose();
    }

    private validate(context: Control, ruleName?: string) {
        this.callQueue.enqueue(() => new Request(this.serializer, path.ui('validate'))
            .before(() => this.busy())
            .args(() => $.extend(this.constructRequestBaseObject(), { ruleName: ruleName }))
            .handle(ValidationSummaryAndDataChanges, (x, args) => {
                this.updateLocalSession(args, x.dataChanges);
                this.afterValidate(x.validationSummary, context);
            })
            .retry(SessionError, () => this.resetSession())
            .final(() => {
                this.notBusy();
                this.callQueue.proceed();
            })
            .post(this.csrfToken())
        );
    }

    private executeActivity(activityName: string, context: string, parameters: ActivityParameter[], onExecutionCompleted: Function, retainResults: string[]) {
        const fileParams = parameters.filter(x => x instanceof ActivityFileParameter).map(x => <ActivityFileParameter>x);
        const nonFileParams = parameters.filter(x => !(x instanceof ActivityFileParameter));

        var hasFiles = fileParams.length > 0;

        var done = activityResult => {
            if (onExecutionCompleted)
                onExecutionCompleted(activityResult);

            this.callQueue.proceed();
        };

        var getArgs = () => $.extend(this.constructRequestBaseObject(), {
            activityName: activityName,
            retainResults: retainResults,
            parameters: nonFileParams
                .filter(x => x.name !== 'FieldContext')
                .concat(new ActivityParameter('FieldContext', context))
        });

        this.callQueue.enqueue(() => new Request(this.serializer, path.ui(!hasFiles ? 'activity' : 'activitywithfiles'))
            .before(() => this.busy())
            .args(getArgs)
            .handleArray(DataRequest, (updates, args) => {
                this.updateLocalSession(args, updates);
                done(updates.filter(y => y instanceof ActivityUpdate)[0]);
            })
            .error(() => done(undefined))
            .retry(SessionError, () => this.resetSession())
            .final(() => {
                this.notBusy();
            })
            .unknown(x => this.handleError(x))
            .post(this.csrfToken(), null, ...fileParams.map(x => new FileParameter(x.name, x.input.files[0])))
        );
    }

    private refresh(done: () => void) {
        this.callQueue.enqueue(() => new Request(this.serializer, path.ui('refresh'))
            .before(() => this.busy())
            .args(() => this.constructRequestBaseObject())
            .handleArray(DataRequest, (updates, args) => this.updateLocalSession(args, updates))
            .retry(SessionError, () => this.resetSession())
            .final(() => {
                this.notBusy();
                this.callQueue.proceed();
                done();
            })
            .post(this.csrfToken())
        );
    }

    private clone() {
        this.callQueue.enqueue(() => new Request(this.serializer, path.ui('clone'))
            .before(() => this.busy())
            .args(() => ({ sourceWorkItemId: this.wivi.workItem.workItemId, definitionName: this.wivi.workItem.definitionName }))
            .handle(WorkItemViewInfo, wivi => {
                this.notBusy();
                this.eventAggregator.publish(new RuntimeHostRequested(wivi));
            })
            .retry(SessionError, () => this.resetSession())
            .post(this.csrfToken())
        );
    }

    private afterValidate(summary: ValidationSummary, context: Control) {
        this.validationSummary = summary;

        var process = (condition: boolean, eventName: string, contextLambda: () => void) => {
            if (!condition)
                return;

            if (context) {
                contextLambda();
            } else {
                this.methods.invoke('Document', eventName, null, () => { });
            }
        };

        process(true, 'validated', () => context.validated());
        process(summary.hasFailed(), 'Validation Failed', () => context.validationFailed());
        process(summary.hasPassed(), 'Validation Succeeded', () => context.validationSucceeded());
        process(summary.hasPassedWithWarnings(), 'Validated with Warnings', () => context.validatedWithWarnings());
    }

    // lookup calls
    private handleLookupItemByDisplayRequested(events: IEventAggregator): ISubscription {
        return events.queryRequest(LookupItemByDisplayRequested).subscribe(x => {
            this.callQueue.enqueue(() => standardRequest(this.serializer, x, path.ui('lookupitembydisplay'))
                .args(() => ({
                    cacheToken: this.wivi.cacheToken,
                    fieldName: x.payload.fieldName,
                    identifier: x.payload.fieldContext,
                    display: x.payload.display
                }))
                .handle(LookupItem, r => x.success(r))
                .handle(null, () => x.success(null))
                .final(() => this.callQueue.proceed())
                .post(this.csrfToken(), x.cancel)
            );
        });
    }

    private handleFilteredLookupItemsRequested(events: IEventAggregator): ISubscription {
        return events.queryRequest(FilteredLookupItemsRequested).subscribe(x => {
            this.callQueue.enqueue(() => standardRequest(this.serializer, x, path.ui('filteredlookupitems'))
                .args(() => ({
                    cacheToken: this.wivi.cacheToken,
                    fieldName: x.payload.fieldName,
                    identifier: x.payload.fieldContext,
                    filter: x.payload.filter
                }))
                .handleArray(LookupItem, r => x.success(r))
                .handle(null, r => x.success([]))
                .final(() => this.callQueue.proceed())
                .post(this.csrfToken(), x.cancel)
            );
        });
    }

    private handleLookupItemsRequested(events: IEventAggregator): ISubscription {
        return events.queryRequest(LookupItemsRequested).subscribe(x => {
            this.callQueue.enqueue(() => standardRequest(this.serializer, x, path.ui('lookupitems'))
                .args(() => ({
                    cacheToken: this.wivi.cacheToken,
                    fieldName: x.payload.fieldName,
                    fieldContext: x.payload.fieldContext
                }))
                .handleArray(LookupItem, r => x.success(r))
                .handle(null, () => x.success([]))
                .final(() => this.callQueue.proceed())
                .post(this.csrfToken(), x.cancel)
            );
        });
    }

    private handleLookupItemRequested(events: IEventAggregator): ISubscription {
        return events.queryRequest(LookupItemRequested).subscribe(x => {
            this.callQueue.enqueue(() => standardRequest(this.serializer, x, path.ui('lookupitem'))
                .args(() => ({
                    cacheToken: this.wivi.cacheToken,
                    fieldName: x.payload.fieldName,
                    identifier: x.payload.fieldContext,
                    keyValue: x.payload.keyValue
                }))
                .handle(LookupItem, r => x.success(r))
                .handle(null, () => x.success(null))
                .final(() => this.callQueue.proceed())
                .post(this.csrfToken(), x.cancel)
            );
        });
    }

    // runtime support routines
    private provideViewInfo(vir: ViewInfoRequested) {
        vir.currentUserId = this.wivi.currentUserId;
        vir.currentUsername = this.wivi.currentUsername;
        vir.currentUserDisplayName = this.wivi.currentUserDisplayName;
        vir.definitionName = this.wivi.workItem.definitionName;
        vir.definitionVersion = this.wivi.workItem.definitionVersion;
        vir.workItemId = this.wivi.workItem.workItemId;
    }

    // image calls
    private crop(request: CropRequested) {
        // crop does NOT block... outstanding upload and crop operations only prevent save and submit operations
        new Request(this.serializer, path.ui('crop'))
            .args(() => ({
                fileId: request.fileId,
                workItemId: request.workItemId,
                imageWidth: request.imageWidth,
                imageHeight: request.imageHeight,
                x: request.x,
                y: request.y,
                width: request.width,
                height: request.height
            }))
            .handle(UploadResult, x => request.callback(x))
            .final(() => {
                this.callQueue.proceed();
            })
            .post(this.csrfToken());
    }

    // support and HOFs for requests
    private constructRequestBaseObject(): any {
        return {
            cacheToken: this.wivi.cacheToken,
            workflowInstanceId: this.wivi.workItem.workflowInstanceId,
            workItemId: this.wivi.workItem.workItemId,
            versionId: this.wivi.workItem.versionId,
            readOnly: this.wivi.workItem.readOnly,
            freshlyCreated: this.wivi.workItem.freshlyCreated,
            nextRequestId: this.requests.current(),
            definitionType: this.wivi.workItem.definitionName,
            dataChanges: this.values.getUpdates()
        };
    }

    private abortIfUploading(callIfAborted: () => void, action: Function) {
        return () => {
            if (this.uploading) {
                this.eventAggregator.publish(new FileUploadPending());
                callIfAborted();
                return;
            }

            action();
        }
    }

    private updateLocalSession(args: any, updates: DataRequest[]) {
        this.requests.increment();

        if (args && args.dataChanges)
            this.values.markAsClean(args.dataChanges.filter(x => x instanceof FieldDataUpdate || x instanceof SessionDataUpdate))

        if (updates)
            this.values.processUpdates(updates);
    }

    private resetSession() {
        this.requests.reset();
        this.values.allValuesDirty();
        this.values.rerequestAllRules();
    }

    // busy logic
    private busy() { this.eventAggregator.publish(new Busy()); }

    private notBusy() { this.eventAggregator.publish(new NotBusy()); }
}